import { action, observable } from 'mobx';
import { FormulaFormVM } from '../template/FormulaFormVM';
import { HydraulicFormulaController } from "cd-eft/services/eftSetApi";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import { OTriangleRequestDto } from 'cd-eft/_codegen_/openapi-generator/eft-set';

interface IOTriangelForm {
  sideA: string,
  sideB: string,
  sideC: string
}
interface ICalculationResult{
  area: string,
  h: string,
  angleA: string,
  angleB: string,
  angleC: string,
  s: string,
  r: string
}
export class ObliqueTriangleVM extends FormulaFormVM {
  readonly formLabelsForExport;
  readonly resultLabelsForExport;
  readonly initialCalculationResults: ICalculationResult = {
    area: '',
    h:'',
    angleA: '',
    angleB: '',
    angleC: '',
    s: '',
    r: ''
  }
  constructor(props) {
    super(props);
    // Any Form specific contructor code. Otherwise, remove contructor - use parent.
    this.disablePrintBtn = true;
    this.formLabelsForExport = {
      sideA: "Side 'a'",
      sideB: "Side 'b'",
      sideC: "Side 'c'",
    }
    this.resultLabelsForExport = {
      area: 'Area (sq units)',
      h: 'Height (units)',
      angleA: 'Angle A°',
      angleB: 'Angle B°',
      angleC: 'Angle C°',
      r: 'Radius (units)',
      s: 'Half of the Perimeter s (units)',
    }
    this.helpLink = 'https://usdagcc.sharepoint.com/:b:/r/sites/FPAC-NRCS-EngineeringPlanningAnalysisandDesignSoftwareEPAD/Shared%20Documents/General/NETS-Support%20Documents/Formulas%20Documentation/Help%20Select%20n%20Value.pdf?csf=1&web=1&e=ssYi6q';
  }
  @observable calculationResults: ICalculationResult = this.initialCalculationResults;
  @observable savedOtriangleFormData: IOTriangelForm = {
    sideA: "0",
    sideB: "0",
    sideC: "0"
  }
  @observable formValidation = {
      hasError: true,
      sideA: false,
      sideB: false,
      sideC: false,
      formErrors: {
          sideA: '',
          sideB: '',
          sideC: '',
      },
  };

  @observable OTriangleFormData: IOTriangelForm = {
    sideA: "3",
    sideB: "4",
    sideC: "5"
  }

  @action.bound handleInputOnChange(event, within?:boolean) {
    const { name } = event.target;
    let value = event.target.value
    if (!within && value.length > 7) {
      return
    }
    switch (name) {
      case 'side-a':
        this.OTriangleFormData.sideA = value;
        break;
      case 'side-b':
        this.OTriangleFormData.sideB = value;
        break;
      case 'side-c':
        this.OTriangleFormData.sideC = value;
        break;
    }
  }

  @action.bound handleOnKeyDown(event) {
    const invalidKeys = ['e', 'E', '+', '-'];
    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
    }
  }
  @action.bound setFixedValue(name, fixed) {
    const event1 = {
     target: {
     name: name,
     value: this.roundingHelperString(fixed, 2)
     }
     }
    this.handleInputOnChange(event1, true);
     }
  @action.bound async validateAllInputsPass(){
    await this.validateInput('side-a', this.OTriangleFormData.sideA);
    await this.validateInput('side-b', this.OTriangleFormData.sideB);
    await this.validateInput('side-c', this.OTriangleFormData.sideC);
    return !this.formValidation.sideA && !this.formValidation.sideB && !this.formValidation.sideC
  }

  @action.bound async validateInput(name, value) {
    let val;
    if (value !== undefined && value !== '') {
      val = this.roundingHelperNumber(parseFloat(value), 2);
    }
    else {
      val = 0;
    }
    if (value !== undefined && value !== '') {
      this.setFixedValue(name, val);
    }

    switch (name) {
      case 'side-a':
        if (value === '' || Number.isNaN(value) || val < 0.01) {
          this.formValidation.sideA = true;
          this.formValidation.formErrors.sideA = ' ';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.sideA)
        }
        else {
          this.formValidation.sideA = false;
          this.formValidation.formErrors.sideA = '';
        }
        break;
      case 'side-b':
        if (value === '' || Number.isNaN(value) || val < 0.01) {
          this.formValidation.sideB = true;
          this.formValidation.formErrors.sideB = ' ';
          await MessageCenter.addNotificationFromCode('Common_10', undefined, this.formLabelsForExport.sideB)
        }
        else {
          this.formValidation.sideB = false;
          this.formValidation.formErrors.sideB = '';
        }
        break;
      case 'side-c':
        if (value === '' || Number.isNaN(value) || val < 0.01) {
          this.formValidation.sideC = true;
          this.formValidation.formErrors.sideC = ' ';
          await MessageCenter.addNotificationFromCode('Common_10', undefined,this.formLabelsForExport.sideC)
        }
        else {
          this.formValidation.sideC = false;
          this.formValidation.formErrors.sideC = '';
        }
        break;
      }
  }

  @action.bound inputsFormClassName(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage?.length === 0 ? '' : ' usa-input--error';
  }

  @action.bound async computeOTriangle(){
    this.disablePrintBtn = true;
    this.disableComputeBtn = true;
    MessageCenter.blockCalls(['Common_26']);
    if(await this.validateAllInputsPass()){
      try{
        const reqPayload: OTriangleRequestDto = {
          a: parseFloat(this.OTriangleFormData.sideA),
          b: parseFloat(this.OTriangleFormData.sideB),
          c: parseFloat(this.OTriangleFormData.sideC)
        }
        const response = await HydraulicFormulaController.calculateOTriangleUsingPOST(reqPayload);
        const responsePayload = response.data;
        if(responsePayload.validationPass){
          this.savedOtriangleFormData = {...this.OTriangleFormData};
          this.calculationResults = {
            area : responsePayload.area!==undefined? this.roundingHelperString(responsePayload.area, 2): '',
            h: responsePayload.height!==undefined? this.roundingHelperString(responsePayload.height, 2): '',
            angleA: responsePayload.angleA!==undefined? this.roundingHelperString(responsePayload.angleA, 2) :'',
            angleB: responsePayload.angleB!==undefined? this.roundingHelperString(responsePayload.angleB, 2) :'',
            angleC : responsePayload.angleC!==undefined? this.roundingHelperString(responsePayload.angleC, 2) :'',
            s: responsePayload.s!==undefined? this.roundingHelperString(responsePayload.s, 2): '',
            r: responsePayload.r!==undefined? this.roundingHelperString(responsePayload.r, 2):'',
          }
          this.hasResults = true;
          this.disablePrintBtn = false;
        }
        else{
          this.hasResults = false;
          this.disablePrintBtn = true;
          this.calculationResults = this.initialCalculationResults;
          MessageCenter.addNotification(responsePayload.errorResponse!.errorMessage!, responsePayload.errorResponse!.errorType!, responsePayload.errorResponse!.errorCode,
            responsePayload.errorResponse?.urlPath ? [{
              text: 'Help',
              callback: () => {
                window.open(responsePayload.errorResponse?.urlPath, '_blank');
              }
            }] : undefined)
        }
      } 
      catch(error){
        console.error('API HydraulicFormulaController.calculateOTriangleUsingPOST error:', error);
      }
      finally {
        this.disableComputeBtn = false;
      }

    }
    else{
      this.hasResults = false;
      this.disablePrintBtn = true;
      this.disableComputeBtn = false;
      MessageCenter.resetMessageCenter();
      await MessageCenter.addNotificationFromCode('Common_26');
    }
    this.disableComputeBtn = false;
    MessageCenter.unblockCalls();
  }

  @action.bound launchAndExport(){
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];

    for (const input in this.savedOtriangleFormData) {
      this.exportedInputs.push({
        label: this.formLabelsForExport[input],
        value: this.savedOtriangleFormData[input]
      })
    }
    for (const result in this.calculationResults) {
      this.exportedResults.push({
        label: this.resultLabelsForExport[result],
        value: this.calculationResults[result]
      })
    }
    this.printFormula();
    this.disablePrintBtn = false;
  }

  @action.bound initializeDefaultValues(){
    this.OTriangleFormData.sideA = this.roundingHelperString(this.defaultValues.a, 2);
    this.OTriangleFormData.sideB = this.roundingHelperString(this.defaultValues.b, 2);
    this.OTriangleFormData.sideC = this.roundingHelperString(this.defaultValues.c, 2);
  }
}